import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { FadeUp, MaskLines } from "./Reveal";

function StatNumber({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, parseInt(value, 10), {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  const suffix = value.replace(/^\d+/, "");
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

const STATS = [
  { n: "120+", l: "Travel companies use our travel CRM" },
  { n: "6", l: "Countries of live travel operations" },
  { n: "20+", l: "Industries served" },
  { n: "100+", l: "Clients using our travel tech products" },
];

const CASES = [
  {
    tag: "Travel · DMC, Southeast Asia",
    t: "From a legacy suite to a direct-booking engine",
    problem: "Expensive enterprise licences, slow quotes and almost no direct bookings.",
    built: "Booking engine, Travel AI CRM and Dynamic Tour Packager.",
    stat: "3.2×",
    statLabel: "more direct bookings within six months.",
    img: "https://images.unsplash.com/photo-1762254794468-1fb6fa893f73?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    tag: "Travel Management Company · India",
    t: "An AI front desk that never misses a call",
    problem: "Missed calls and slow replies during peak season.",
    built: "AI Caller and AI Chat Bot across phone, web and WhatsApp.",
    stat: "68%",
    statLabel: "of enquiries now resolved without a human.",
    img: "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "The AI Caller books more meetings in a week than our old team did in a month. It just doesn't take days off.",
    name: "Rohit Menon",
    role: "Director, Skylark Holidays",
  },
  {
    quote:
      "We replaced four tools with their travel stack. Quotes that took two days now take two minutes.",
    name: "Sarah D'Souza",
    role: "Head of Operations, Nomad Trails DMC",
  },
  {
    quote:
      "The CRM paid for itself in the first quarter, and their support answers faster than our own staff.",
    name: "Arjun Nair",
    role: "Founder, Wanderfolk Travels",
  },
];

export default function Proof() {
  return (
    <section id="proof" className="scroll-mt-20 bg-paper py-24 text-[#0f0f14] md:py-28">
      <div className="mx-auto max-w-[1160px] px-6">
        <FadeUp>
          <p
            className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand"
            data-testid="proof-eyebrow"
          >
            06 — Proof
          </p>
        </FadeUp>

        <MaskLines
          lines={["Measured in outcomes."]}
          className="mt-8 text-[clamp(2.5rem,5.4vw,4.6rem)] font-normal leading-[1.04] tracking-[-0.025em]"
        />

        <div className="mt-20 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <FadeUp key={s.l} delay={i * 0.08} className="border-t border-black/[0.12] pt-9">
              <p
                className="text-[clamp(3rem,4.6vw,4.2rem)] font-normal leading-none tracking-[-0.02em] text-brand"
                data-testid={`proof-stat-${i + 1}`}
              >
                <StatNumber value={s.n} />
              </p>
              <p className="mt-4 max-w-[240px] text-[16px] leading-[1.5] text-[#54545c]">{s.l}</p>
            </FadeUp>
          ))}
        </div>

        <div className="mt-20 grid gap-8 lg:grid-cols-2">
          {CASES.map((c, i) => (
            <FadeUp key={c.t} delay={i * 0.1} className="h-full">
              <div
                className="flex h-full flex-col rounded-xl border border-black/[0.08] bg-white p-6 md:p-7"
                data-testid={`case-study-${i + 1}`}
              >
                <div className="aspect-[16/9] w-full overflow-hidden rounded-md border border-black/[0.06] bg-[#E8E6E1]">
                  <img
                    src={c.img}
                    alt={c.t}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
                  />
                </div>
                <p className="mt-8 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#8a8a90]">
                  {c.tag}
                </p>
                <h3 className="mt-4 text-[clamp(1.6rem,2.3vw,2.1rem)] font-medium leading-[1.2] tracking-[-0.015em]">
                  {c.t}
                </h3>
                <p className="mt-7 text-[13px] text-[#8a8a90]">Problem</p>
                <p className="mt-1.5 text-[17px] leading-[1.5]">{c.problem}</p>
                <p className="mt-5 text-[13px] text-[#8a8a90]">What we built</p>
                <p className="mt-1.5 text-[17px] leading-[1.5]">{c.built}</p>
                <div className="mt-auto pt-9">
                  <div className="border-t border-black/10 pt-8">
                    <p className="text-[clamp(2.2rem,3vw,3rem)] font-normal tracking-[-0.02em] text-brand">
                      {c.stat}
                    </p>
                    <p className="mt-2 text-[16px] text-[#54545c]">{c.statLabel}</p>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.05} className="mt-24">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#8a8a90]" data-testid="testimonials-label">
            What clients say
          </p>
        </FadeUp>
        <div className="mt-10 grid gap-8 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.08} className="h-full">
              <figure
                className="flex h-full flex-col rounded-xl border border-black/[0.08] bg-white p-7"
                data-testid={`testimonial-${i + 1}`}
              >
                <span className="text-[44px] leading-none text-brand" aria-hidden="true">
                  &ldquo;
                </span>
                <blockquote className="mt-2 text-[17px] leading-[1.6] text-[#0f0f14]/90">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-auto pt-7">
                  <p className="text-[16px] font-medium">{t.name}</p>
                  <p className="mt-0.5 text-[14px] text-[#8a8a90]">{t.role}</p>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
